export type LoanStatus =
  | "new_lead"
  | "in_progress"
  | "ip_sanction"
  | "logged_in"
  | "query"
  | "sanctioned"
  | "verification_pending"
  | "disbursal_pending"
  | "partial_disbursed"
  | "disbursed"
  | "otc"
  | "payout_released"
  | "closed"
  | "removed"
  | "followup_later"
  | "rejected";

const LEGACY_LOAN_STATUS_ALIASES: Record<string, LoanStatus> = {
  "new lead": "new_lead",
  processing: "in_progress",
  sanctioned: "sanctioned",
  disbursed: "disbursed",
  "logged in": "logged_in",
  "verification pending": "verification_pending",
  "disbursal pending": "disbursal_pending",
  "partial disbursed": "partial_disbursed",
  "payout released": "payout_released",
  "followup later": "followup_later",
  rejected: "rejected",
};

export function normalizeLoanStatus(status: unknown): LoanStatus {
  if (typeof status !== "string") return "new_lead";
  const normalized = status.trim();
  if (!normalized) return "new_lead";
  if ((normalized as LoanStatus) in LOAN_STATUS_CONFIG) {
    return normalized as LoanStatus;
  }
  return LEGACY_LOAN_STATUS_ALIASES[normalized.toLowerCase()] || "new_lead";
}

export type UserEmploymentType = "salary" | "freelance";

export type EmploymentType =
  | "salaried"
  | "self_employed_itr"
  | "self_employed_pro";

export type LoanType =
  | "home_loan"
  | "lap"
  | "personal_loan"
  | "business_loan"
  | "car_loan";

export type InsuranceType = "life" | "health" | "motor" | "general";

export type LeadCategory = "loan" | "insurance";

export type UserRole =
  | "member"
  | "operations_user"
  | "sales_agent"
  | "admin"
  | "app_user"
  | "relational_manager"
  | "basic_user";

export enum Permission {
  VIEW_LEADERBOARD = "view_leaderboard",
  MANAGE_COMMITMENT = "manage_commitment",
  ACCESS_BACKEND_TOOLS = "access_backend_tools",
  ACCESS_ADMIN_TOOLS = "access_admin_tools",
  SHOW_IN_BROADCAST = "show_in_broadcast",
  EDIT_SYSTEM_PRODUCT_NOTES = "edit_system_product_notes",
}

// Type for permission values (for compatibility with existing code)
export type PermissionValue = `${Permission}`;

export type RoleBadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline";

export interface RoleConfig {
  id: UserRole;
  name: string;
  description: string;
  permissions: PermissionValue[];
  label: string;
  variant: RoleBadgeVariant;
  displayOrder: number;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export type ConnectorType =
  | "broker"
  | "builder"
  | "channel_partner"
  | "ca"
  | "financial_advisor"
  | "connector"
  | "dsa"
  | "hlc"
  | "banker"
  | "others";

export type DsaPartnerType =
  | "dsa"
  | "hlc"
  | "banker"
  | "financial_advisor"
  | "others";

export type TimelineItemType =
  | "note"
  | "reminder"
  | "status_change"
  | "assignment";

export type TaskType = "task" | "reminder";

export interface ApprovedUser {
  id: string; // Document ID
  phone: string;
  userId: string;
  firebaseUid?: string; // Firebase Auth UID (linked after first login)
  name: string;
  email?: string;
  roles: UserRole[]; // Support multiple roles
  primaryRole: UserRole; // Main role for the user
  officeId: string; // Required - office assignment
  isActive: boolean;
  // Employee details
  employeeId?: string;
  designation?: string;
  department?: string;
  joiningDate?: Date | string;
  reportingTo?: string;
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
  };
  // Metadata
  lastTokenUpdate?: string;
  teamId?: string;
  fcmTokens?: string[]; // FCM tokens for push notifications
  createdAt?: Date;
  updatedAt?: Date;
  deactivatedAt?: Date;
  reactivatedAt?: Date;
}

// Form data type for user creation/editing
export interface UserFormData {
  name: string;
  phone: string; // Changed from phoneNumber for consistency
  email: string;
  officeId: string;
  roles: UserRole[];
  primaryRole: UserRole;
  employeeId?: string;
  designation?: string;
  department?: string;
  joiningDate?: string;
  reportingTo?: string;
  emergencyContact?: {
    // Changed to nested object
    name: string;
    relationship: string;
    phone: string;
  };
}

// Generic document/file attachment interface (used across different features)
export interface DocumentAttachment {
  id: string;
  name: string;
  type: string; // MIME type (e.g., 'application/pdf', 'image/jpeg')
  size: number; // File size in bytes
  url?: string; // Local data URL or Firebase storage URL
  uploadedAt?: Date;
  uploadedBy?: string;
}

// Alias for backward compatibility with tasks
export interface TaskAttachment extends DocumentAttachment {}

export interface TimelineItem {
  id: string;
  type: TimelineItemType;
  content: string;
  createdAt: Date;
  createdBy: string;
  dueDate?: Date;
  completed?: boolean;
  archived?: boolean;
  isDailyUpdate?: boolean;
}

export interface ChecklistItem {
  id: string;
  label: string;
  checked: boolean;
}

export interface LoginRecord {
  id: string;
  customerName: string;
  loanType: string;
  applicationNumber: string;
  loanAmount: number;
  bankName: string;
  bankerName: string;
  bankerId?: string;
  remarks?: string;
  isMain?: boolean; // True = this record drives lead fields & leaderboard attribution; exactly one per lead should be main
  createdAt: Date;
  createdBy: string;
  updatedAt?: Date;
  updatedBy?: string;
}

export interface DisbursementRecord {
  id: string;
  type: "partial" | "full";
  isFinal: boolean; // Is this the final partial disbursement?

  // Financial Details
  disbursedAmount: number;
  sanctionedAmount?: number; // Reference from lead

  // Dates
  disbursementDate: Date;
  salesConsiderationDate?: Date;

  // References
  referenceNumber?: string;
  bankUTR?: string;

  // Bank & Banker
  bankName?: string; // Bank name from config.banks
  bankerId?: string; // SMContact / Banker ID
  bankerName?: string; // Denormalized banker name for display

  // Sourcing
  sourcingType?: "self" | "vendor"; // Self sourced or vendor sourced
  vendorId?: string; // Channel partner ID (if vendor sourced)
  vendorName?: string; // Denormalized vendor name for display

  // Payout
  vendorPayout?: number; // 0 to 1 decimal (e.g., 0.25 for 25%)

  // Metadata
  remarks?: string;
  createdAt: Date;
  createdBy: string;
  updatedAt?: Date;
  updatedBy?: string;

  // Documents
  documents?: DocumentAttachment[];
}

export interface Lead {
  id: string;
  category: LeadCategory;
  // Common fields
  firstName: string;
  lastName: string;
  mobile: string;
  secondaryNumber?: string;
  email?: string;
  dateOfBirth?: Date;
  panCard?: string;
  pincode?: string;
  cibilScore?: number;
  // Loan specific
  loanType?: LoanType;
  loanAmount?: number;
  loanTenure?: number;
  lender?: string;
  customerCity?: string;
  // NRI / Residency
  isNRI?: boolean; // true = NRI, false/undefined = Indian Resident
  nriCountry?: string; // Country of residence for NRI customers
  // Property Details (Home Loan / LAP)
  isPropertyIdentified?: boolean; // Whether the property has been identified
  propertyType?: string;
  propertyValue?: number;
  propertyPincode?: string;
  propertyCity?: string;
  propertyAddress?: string;
  employmentType?: EmploymentType;
  monthlySalary?: number;
  // BT & Top-Up fields
  isBT?: boolean; // Balance Transfer case
  isTopUp?: boolean; // Top-up case
  existingLender?: string; // For BT cases
  outstandingAmount?: number; // For BT cases
  currentInterestRate?: number; // For BT cases
  remainingTenure?: number; // For BT cases (in years)
  topUpAmount?: number; // For top-up cases
  // Additional fields
  companyName?: string; // Company or business name
  yearsInCurrentJob?: number; // Employment stability
  leadSource?:
    | "direct"
    | "referral"
    | "website"
    | "social_media"
    | "channel_partner"
    | "other"; // Lead source tracking
  // Insurance specific
  insuranceType?: InsuranceType;
  sumAssured?: number;
  // Status & assignment
  status: LoanStatus;
  assignedTo: string;
  createdBy?: string; // User ID who created the lead
  sharedWith?: string[]; // Array of user IDs who have access
  assignedBank?: string;
  sanctionedAmount?: number;
  disbursedAmount?: number;
  // Disbursement details
  payoutPercentage?: number;
  vendorPayoutPercentage?: number;
  salesConsiderationDate?: Date;
  vendorPayout?: number; // 0 to 1 decimal
  // Channel partner
  connectorId?: string;
  // Banker Contact
  smContactId?: string;
  // Location
  location?: { lat: number; lng: number };
  // Timeline & checklist
  timeline: TimelineItem[];
  checklist: ChecklistItem[];
  // Form data persistence
  questionnaireData?: {
    loanType: LoanType;
    completedSections: string[];
    lastUpdated: Date;
    // Property Details (Home Loan / LAP)
    propertyType?: string;
    propertyStatus?: string;
    propertyValue?: number;
    propertyAge?: number;
    propertyAddress?: string;
    propertyCity?: string;
    propertyPincode?: string;
    builtUpArea?: number;
    plotArea?: number;
    // Purchase/Construction Details (Home Loan / LAP)
    purchaseType?: string;
    sellerDetails?: string;
    agreementValue?: number;
    expectedRegistrationDate?: string;
    constructionStage?: string;
    constructionCost?: number;
    builderName?: string;
    builderRegistration?: string;
    estimatedCompletionDate?: string;
    // Co-Applicant Details
    hasCoApplicant?: string;
    coApplicantName?: string;
    coApplicantRelation?: string;
    coApplicantDOB?: string;
    coApplicantPAN?: string;
    coApplicantPhone?: string;
    coApplicantEmployment?: string;
    coApplicantIncome?: number;
    // Income & Employment (All loan types)
    employmentType?: string;
    employerName?: string;
    designation?: string;
    experienceYears?: number;
    officeAddress?: string;
    officeEmail?: string;
    officePhone?: string;
    monthlySalary?: number;
    takeHomeSalary?: number;
    variableIncome?: number;
    otherIncome?: number;
    otherIncomeSource?: string;
    salaryMode?: string;
    salaryBankName?: string;
    salaryAccountNumber?: string;
    previousEmployer?: string;
    previousExperienceYears?: number;
    selfEmployedBusinessName?: string;
    businessType?: string;
    businessVintage?: number;
    annualTurnover?: number;
    annualProfit?: number;
    gstRegistered?: string;
    gstNumber?: string;
    // Existing Liabilities
    hasExistingLoans?: string;
    homeLoanEMI?: number;
    carLoanEMI?: number;
    personalLoanEMI?: number;
    creditCardEMI?: number;
    otherEMI?: number;
    totalMonthlyEMI?: number;
    outstandingCreditCardDues?: number;
    // Banking & Financial Details
    primaryBankName?: string;
    primaryAccountNumber?: string;
    averageMonthlyBalance?: number;
    bankingRelationshipYears?: number;
    hasOverdraftFacility?: string;
    hasChequeReturns?: string;
    chequeReturnCount?: number;
    // Business Details (Business Loan)
    businessName?: string;
    businessEntityType?: string;
    businessAddress?: string;
    businessPincode?: string;
    businessVintageYears?: number;
    numberOfEmployees?: number;
    businessPAN?: string;
    businessGST?: string;
    natureOfBusiness?: string;
    lastYearTurnover?: number;
    lastYearProfit?: number;
    currentYearTurnover?: number;
    businessBankAccount?: string;
    businessAccountBalance?: number;
    loanPurpose?: string;
    loanAmountRequired?: number;
    proposedRepaymentTenure?: number;
    businessBankName?: string;
    businessAccountNumber?: string;
    // Gold Loan Details
    goldType?: string;
    goldWeight?: number;
    goldPurity?: string;
    estimatedValue?: number;
    loanAgainstGoldAmount?: number;
    goldOwnership?: string;
    goldPurpose?: string;
    goldRepaymentPeriod?: number;
    repaymentSource?: string;
    // Education Loan Details
    studentName?: string;
    studentDOB?: string;
    studentRelation?: string;
    courseName?: string;
    courseDuration?: number;
    instituteName?: string;
    instituteCountry?: string;
    instituteCity?: string;
    admissionDate?: string;
    tuitionFees?: number;
    otherFees?: number;
    totalCourseFees?: number;
    lastQualification?: string;
    lastQualificationMarks?: number;
    entranceExamScore?: string;
    hasScholarship?: string;
    scholarshipAmount?: number;
    // Personal Loan Purpose
    personalLoanPurpose?: string;
    personalLoanPurposeOther?: string;
    // Documents Checklist
    documentsChecklist?: string[];
  };
  disbursementData?: unknown; // Stores disbursement form data (deprecated, use disbursements array)
  // Login Records
  loginRecords?: LoginRecord[]; // Array of login form submissions
  // Multi-Disbursement Support
  disbursements?: DisbursementRecord[]; // Array of all disbursements
  totalDisbursed?: number; // Auto-calculated: sum of all disbursed amounts
  remainingAmount?: number; // Auto-calculated: sanctionedAmount - totalDisbursed
  disbursementProgress?: number; // Auto-calculated: percentage (0-100)
  createdAt: Date;
  updatedAt: Date;
}

// Legacy support
export const getLegacyCustomerName = (
  lead: Partial<Lead> & { fullName?: string; name?: string }
) =>
  ([lead.firstName, lead.lastName].filter(Boolean).join(" ").trim()) ||
  lead.fullName ||
  lead.name ||
  "Unknown";

export interface Bank {
  id: string;
  name: string;
  logo: string;
  minCibil: number;
  minSalary: number;
  minExperience: string;
  employmentTypes: EmploymentType[];
  roiRange: { min: number; max: number };
  processingFee: string;
  maxTenure: number;
  multiplier: string;
  negativeProfiles: string[];
  negativeAreas: string[];
  ltvLimit: string;
  avgTat: string;
  tags: string[];
  contactPerson?: { name: string; phone: string };
}

export interface PartnerDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string; // Firebase Storage URL
}

export interface Connector {
  id: string;
  name: string;
  type: ConnectorType;
  businessName?: string;
  whatsappNumber: string;
  secondaryNumber?: string;
  address: string;
  pincode: string;
  city: string;
  state: string;
  email?: string;
  visitingCardPhoto?: string;
  location?: { lat: number; lng: number };
  isFavorite: boolean;
  lastVisitDate?: Date;
  lastVisitedBy?: string; // User ID who last visited the vendor
  lastFollowUpDate?: Date;
  totalVisits: number;
  createdAt: Date;
  updatedAt?: Date;
  createdBy?: string; // User ID who created the channel partner
  sharedWith?: string[]; // Array of user IDs who have shared access
  documents?: PartnerDocument[];
  remark?: string;
  kycVerified?: boolean;
  isActive?: boolean; // false = deactivated
  deactivatedAt?: Date;
  deactivationReason?: string;
}

export interface DsaPartner extends Omit<Connector, "type"> {
  type: DsaPartnerType;
}

export interface BankerDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string; // Firebase Storage URL
}

export interface SMContact {
  id: string;
  name: string;
  bank: string;
  phone: string;
  email?: string;
  region: string;
  designation?: string;
  products: string[]; // Array of product types
  reportReason?: string; // Report functionality
  isReported?: boolean; // Report status
  remark?: string;
  location?: { lat: number; lng: number };
  documents?: BankerDocument[];
  createdBy: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface UserBankerFavorite {
  userId: string;
  bankerId: string;
  createdAt: Date;
}

export interface User {
  id: string; // Maps to approvedUser.userId
  documentId?: string; // Firestore document ID
  userId?: string; // User ID (same as id, e.g., "test_dummy_user_001")
  phone?: string; // User's phone number for display
  name: string;
  email: string;
  roles?: UserRole[]; // Support multiple roles
  primaryRole?: UserRole; // Main role for the user
  officeId?: string; // Office assignment
  avatar?: string;
  teamId?: string;
  monthlyTarget: number;
  monthlyTargetCount: number;
  loginTarget: number;
  loginTargetCount: number;
  currentDisbursement: number;
  currentDisbursementCount: number;
  currentLogin: number;
  currentLoginCount: number;
  commission: number;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  type: TaskType;
  leadId?: string;
  connectorId?: string;
  smContactId?: string;
  dueDate: Date;
  priority: "low" | "medium" | "high";
  completed: boolean;
  attachments?: TaskAttachment[];
  sharedWith?: string[]; // Banker IDs for sharing
  createdBy: string; // dsaUserId — used for security rules & queries
  createdByName?: string; // Denormalized display name of creator
  createdAt: Date;
  updatedAt?: Date;
  // Snooze tracking
  originalDueDate?: Date; // Original due date before any snoozes
  snoozeCount?: number; // Number of times this task has been snoozed
  lastSnoozedAt?: Date; // When the last snooze occurred
}

export interface LeaderboardEntry {
  rank: number;
  userId?: string;
  name: string;
  amount: number;
  target: number;
  count: number;
  targetCount: number;
}

type LegacyLoanStatus =
  | "New Lead"
  | "Processing"
  | "Sanctioned"
  | "Disbursed"
  | "Logged In"
  | "Verification Pending"
  | "Disbursal Pending"
  | "Partial Disbursed"
  | "Payout Released"
  | "Followup Later"
  | "Rejected";

export const LOAN_STATUS_CONFIG: Record<
  LoanStatus | LegacyLoanStatus,
  { label: string; color: string; badgeVariant: string }
> = {
  new_lead: { label: "New Lead", color: "info", badgeVariant: "lead" },
  in_progress: { label: "In Discussion", color: "info", badgeVariant: "info" },
  ip_sanction: {
    label: "IP Sanction",
    color: "accent",
    badgeVariant: "accent",
  },
  logged_in: { label: "Logged In", color: "warning", badgeVariant: "login" },
  query: { label: "Query", color: "destructive", badgeVariant: "query" },
  sanctioned: {
    label: "Sanctioned",
    color: "accent",
    badgeVariant: "sanction",
  },
  verification_pending: {
    label: "Verification",
    color: "warning",
    badgeVariant: "warning",
  },
  disbursal_pending: {
    label: "Disbursal Pending",
    color: "accent",
    badgeVariant: "accent",
  },
  partial_disbursed: {
    label: "Partial Disbursed",
    color: "success",
    badgeVariant: "success",
  },
  disbursed: {
    label: "Disbursed",
    color: "success",
    badgeVariant: "disbursed",
  },
  otc: { label: "OTC Done", color: "gold", badgeVariant: "gold" },
  payout_released: {
    label: "Payout Released",
    color: "success",
    badgeVariant: "success",
  },
  closed: { label: "Closed", color: "secondary", badgeVariant: "secondary" },
  removed: {
    label: "Removed",
    color: "destructive",
    badgeVariant: "destructive",
  },
  followup_later: {
    label: "Followup Later",
    color: "muted",
    badgeVariant: "outline",
  },
  rejected: {
    label: "Rejected",
    color: "destructive",
    badgeVariant: "destructive",
  },

  // Legacy Firestore values
  "New Lead": { label: "New Lead", color: "info", badgeVariant: "lead" },
  Processing: { label: "In Discussion", color: "info", badgeVariant: "info" },
  Sanctioned: {
    label: "Sanctioned",
    color: "accent",
    badgeVariant: "sanction",
  },
  Disbursed: {
    label: "Disbursed",
    color: "success",
    badgeVariant: "disbursed",
  },
  "Logged In": { label: "Logged In", color: "warning", badgeVariant: "login" },
  "Verification Pending": {
    label: "Verification",
    color: "warning",
    badgeVariant: "warning",
  },
  "Disbursal Pending": {
    label: "Disbursal Pending",
    color: "accent",
    badgeVariant: "accent",
  },
  "Partial Disbursed": {
    label: "Partial Disbursed",
    color: "success",
    badgeVariant: "success",
  },
  "Payout Released": {
    label: "Payout Released",
    color: "success",
    badgeVariant: "success",
  },
  "Followup Later": {
    label: "Followup Later",
    color: "muted",
    badgeVariant: "outline",
  },
  Rejected: {
    label: "Rejected",
    color: "destructive",
    badgeVariant: "destructive",
  },
};

export const LOAN_TYPE_LABELS: Record<LoanType, string> = {
  home_loan: "Home Loan",
  lap: "LAP",
  personal_loan: "Personal Loan",
  business_loan: "Business Loan",
  car_loan: "Car Loan",
};

const LOAN_TYPE_ALIASES: Record<string, LoanType> = {
  loan_against_property: "lap",
};

/**
 * Some older leads have `loanType` stored as a human-readable label (e.g.
 * "Home Loan") instead of the canonical enum key ("home_loan") — this
 * normalizes either form back to a valid LoanType key, falling back to the
 * original value unchanged if it doesn't match anything known.
 */
export function normalizeLoanType(value: string | undefined | null): LoanType | undefined {
  if (!value) return undefined;
  const slug = String(value).trim().toLowerCase().replace(/\s+/g, "_");
  if ((Object.keys(LOAN_TYPE_LABELS) as string[]).includes(slug)) return slug as LoanType;
  if (LOAN_TYPE_ALIASES[slug]) return LOAN_TYPE_ALIASES[slug];
  return value as LoanType;
}

export const CONNECTOR_TYPE_LABELS: Record<ConnectorType, string> = {
  broker: "Broker",
  builder: "Builder",
  channel_partner: "Channel Partner",
  ca: "CA",
  financial_advisor: "Financial Advisor",
  connector: "Connector",
  dsa: "DSA",
  hlc: "HLC",
  banker: "Banker",
  others: "Others",
};

export const DSA_PARTNER_TYPE_LABELS: Record<DsaPartnerType, string> = {
  dsa: "DSA",
  hlc: "HLC",
  financial_advisor: "Financial Advisor",
  banker: "Banker",
  others: "Others",
};

// ============================================================================
// EXTERNAL TYPE EXPORTS
// ============================================================================

// Export questionnaire types
export type {
  FieldType,
  ConditionalOperator,
  ConditionalAction,
  FieldValidation,
  ConditionalRule,
  ConditionalLogic,
  FieldOption,
  TemplateField,
  TemplateSection,
  QuestionnaireTemplate,
  QuestionnaireForm,
  QuestionnaireResponse,
  MergedQuestionnaireForm,
} from "./questionnaire";

// Export checklist types (with aliasing to avoid conflicts)
export type {
  ChecklistItem as ChecklistItemTemplate,
  ChecklistCategory,
  ChecklistTemplate,
  ChecklistForm,
  ChecklistResponse,
  MergedChecklistForm,
  ChecklistStats,
  CategoryStats,
} from "./checklist";

// Auth Context Type
export interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  sendOTP: (phone: string) => Promise<void>;
  verifyOTP: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  setConfirmationResult: (result: unknown) => void;
}

// Re-export product notes types
export * from "./productNotes";
